import { dispatch, getState } from '../../store';
import AuthActions from './actions';
import firebaseApp from '../../services/firebaseService';
import generalFunctions from '../general/functions';
import HttpService from '../../services/httpService';
import { IEmployee } from '../../@types/employee';
import HttpGeoServerService from '../../services/http/geoserverHttpService';

export default class AuthFunctions {
    private static employeeUnsubscribe ?: () => void;

    public static readonly initialize = async (firebaseUser : firebase.User, employee : IEmployee) => {
        const token = await firebaseUser.getIdTokenResult(true);

        HttpService.initializeInterceptor(token.token);
        HttpGeoServerService.initializeInterceptor();
        dispatch(AuthActions.authSetSession({
            employee,
            firebaseUser,
        }));
        dispatch(AuthActions.authSetLoggedIn(true));
        dispatch(AuthActions.authSetLoggingIn(false));
    };

    public static readonly logoutUser = async () => {
        try {
            dispatch(AuthActions.authSetLoggingIn(true));

            await firebaseApp.auth().signOut();
            
            dispatch(AuthActions.authSetSession(null));
            dispatch(AuthActions.authSetLoggedIn(false));
            dispatch(AuthActions.authSetLoggingIn(false));
            HttpService.initializeInterceptor('');

            if (AuthFunctions.employeeUnsubscribe) AuthFunctions.employeeUnsubscribe();
        } catch (ex) {
            generalFunctions.generalShowErrorSnackbar('An error occurred while trying to sign you out');
        } finally {
            dispatch(AuthActions.authSetLoggingIn(false));
        }

    };

    public static readonly hasRight = (url : string, search ?: string) => {
        const authState = getState().auth;

        if (!authState.session) return false;

        if (url.startsWith('/masterData/pumpingStation/')) {
            return !!authState.session.employee.IsPumpingStationAdmin;
        }

        switch (url) {
            case '/borehole/list':
            case '/borehole/map':
                return !!authState.session.employee.IsBoreholeUser;
            case '/transformer/capturing/spu':
                return !!authState.session.employee.IsTransformerSpuTariff;
            case '/transformer/capturing/lpu':
                return !!authState.session.employee.IsTransformerLpuTariff;
            case '/transformer/capturing/spu/meter':
                return !!authState.session.employee.IsTransformerSpuTester;
            case '/transformer/capturing/lpu/meter':
                return !!authState.session.employee.IsTransformerLpuTester;
            case '/transformer/capturing/municipality/meter':
                return !!authState.session.employee.IsTransformerMunicipalityTester;
            case '/transformer/capturing/municipality/invoice':
            case '/transformer/capturing/municipality/invoice/capture':
                return !!authState.session.employee.IsMunicipalityInvoice;
            case '/transformer/capturing/sps/invoice':
            case '/transformer/capturing/sps/invoice/capture':
                return !!authState.session.employee.IsSPSInvoice;
            case '/transformer/capturing/prepaid/meter':
                return !!authState.session.employee.IsTransformerPrepaidTester;
            case '/transformer/capturing/spu/invoice':
                return !!authState.session.employee.IsTransformerSpuTariff;
            case '/transformer/capturing/lpu/invoice':
                return !!authState.session.employee.IsTransformerLpuTariff;
            case '/transformer/list':
            case '/transformer/map':
                return !!authState.session.employee.IsTransformerUser;
            case '/transformer/journals':
            case '/transformer/bankAccounts':
                return !!authState.session.employee.IsTransformerAdmin;
            case '/transformer/budget':
            case '/transformer/budget/csv':
                return !!authState.session.employee.IsTransformerBudgetAdmin;
            case '/transformer/tariffs':
                return !!authState.session.employee.IsTransformerTariffAdmin;
            case '/weather/list':
            case '/weather/map':
                return !!authState.session.employee.IsWeatherStationUser;
            case '/dam/list':
            case '/dam/map':
                return !!authState.session.employee.IsDamUser;
            case '/pumpingStation/list':
            case '/pumpingStation/map':
                return !!authState.session.employee.IsPumpingStationUser;
            case '/river/list':
            case '/river/map':
                return !!authState.session.employee.IsRiverUser;
            case '/employee':
                return !!authState.session.employee.IsEmployeeAdmin;
            case '/division':
                return !!authState.session.employee.IsDivisionAdmin;
            case '/masterData/company':
                return !!authState.session.employee.IsCompanyAdmin;
            case '/masterData/valueAddedTax':
                return !!authState.session.employee.IsValueAddedTaxAdmin;
            case '/masterData/groundWaterZones':
                return !!authState.session.employee.IsGroundWaterZoneAdmin;
            case '/masterData/groundWaterZones':
            case '/masterData/pumps':
            case '/masterData/motors':
            case '/masterData/motorSizes':
            case '/masterData/boreholeIssues':
            case '/borehole/runtimesOverride':
                return !!authState.session.employee.IsBoreholeMasterDataAdmin;
            case '/masterData/transformer/group':
            case '/masterData/transformer/usage':
                return !!authState.session.employee.IsTransformerMasterDataAdmin;
            case '/flowmeter/list':
            case '/flowmeter/map':
                return !!authState.session.employee.IsFlowmeterUser;
            case '/chemicalFillingPoint/list':
            case '/chemicalFillingPoint/map':
                return !!authState.session.employee.IsFillingPointUser;
            case '/rainMeter/rainMeterManager':
                return (!!authState.session.employee.IsRainMeterAdmin 
                    || !!authState.session.employee.IsRainMeterUser 
                    || !!authState.session.employee.IsRainMeterMeasurementUser);
            case '/development/tools':
                return !!authState.session.employee.IsDevelopmentToolsAdmin;
        }

        return false;
    };
}
